import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingBagIcon } from '@heroicons/react/24/outline';
import { useUIStore } from '@/stores/uiStore';
import { useOrders, useBulkUpdateOrders, useUpdateOrderStatus } from '@/hooks/useOrders';
import { Card } from '@/components/ui/Card';
import { OrderStatusBadge } from '@/components/ui/Badge';
import { SearchInput } from '@/components/ui/SearchInput';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Pagination } from '@/components/ui/Pagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { SkeletonTable } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Dropdown } from '@/components/ui/Dropdown';
import { formatCurrency, formatRelativeTime, formatDate } from '@/utils/format';
import { type OrderStatus } from '@wooconnect/shared';
import { useOrderStatuses } from '@/hooks/useOrderStatuses';

export function OrdersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeSiteId = useUIStore((s) => s.activeSiteId);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<OrderStatus | null>(null);

  // Reset selections on store switch
  useEffect(() => {
    setSelected(new Set());
    setPage(1);
  }, [activeSiteId]);

  const filters = { page, perPage, search: search || undefined, status: statusFilter || undefined, after: dateFrom || undefined, before: dateTo || undefined };
  const { data, isLoading, isError, refetch } = useOrders(activeSiteId, filters);
  const bulkMutation = useBulkUpdateOrders(activeSiteId ?? '');

  const orderStatuses = useOrderStatuses(activeSiteId);

  const statusOptions = [
    { value: '', label: t('common.all') },
    ...orderStatuses,
  ];

  const handleSearch = useCallback((val: string) => {
    setSearch(val);
    setPage(1);
  }, []);

  const toggleSelect = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    if (!data?.items) return;
    if (selected.size === data.items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(data.items.map((o) => o.id)));
    }
  };

  const handleBulkConfirm = async () => {
    if (!bulkStatus || !activeSiteId) return;
    await bulkMutation.mutateAsync({ orderIds: [...selected], status: bulkStatus });
    setSelected(new Set());
    setBulkStatus(null);
  };

  if (!activeSiteId) {
    return <EmptyState icon={ShoppingBagIcon} title={t('dashboard.selectStore')} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('orders.title')}</h1>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={search}
          onChange={handleSearch}
          placeholder={`${t('common.search')}...`}
          className="w-full sm:w-64"
        />
        <Select
          options={statusOptions}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
          placeholder={t('orders.status')}
          className="w-full sm:w-44"
        />
        <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} placeholder={t('common.from')} className="w-full sm:w-40" />
        <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} placeholder={t('common.to')} className="w-full sm:w-40" />
      </div>

      {/* Table */}
      <Card padding="sm">
        {isLoading ? (
          <SkeletonTable rows={10} cols={5} />
        ) : isError ? (
          <ErrorState onRetry={() => refetch()} />
        ) : data && data.items.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.size === data.items.length && data.items.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                      />
                    </th>
                    <th className="px-4 py-3">{t('orders.order')}</th>
                    <th className="px-4 py-3">{t('orders.customer')}</th>
                    <th className="px-4 py-3">{t('orders.total')}</th>
                    <th className="px-4 py-3">{t('orders.status')}</th>
                    <th className="px-4 py-3">{t('orders.date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((order) => (
                    <tr
                      key={order.id}
                      className="border-b border-gray-50 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50"
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(order.id)}
                          onChange={() => toggleSelect(order.id)}
                          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                        />
                      </td>
                      <td
                        className="cursor-pointer px-4 py-3 font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
                        onClick={() => navigate(`/orders/${order.id}`)}
                      >
                        #{order.number}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900 dark:text-gray-100">{order.customerName}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{order.customerEmail}</div>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                        {formatCurrency(order.total, order.currency)}
                      </td>
                      <td className="px-4 py-3"><OrderStatusBadge status={order.status} statusLabel={order.statusLabel} /></td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400" title={formatDate(order.dateCreated, 'dd/MM/yyyy HH:mm')}>
                        {formatRelativeTime(order.dateCreated)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="mt-4 px-4">
              <Pagination
                page={data.page}
                perPage={data.perPage}
                total={data.total}
                totalPages={data.totalPages}
                onPageChange={setPage}
                onPerPageChange={(pp) => { setPerPage(pp); setPage(1); }}
              />
            </div>
          </>
        ) : (
          <EmptyState
            icon={ShoppingBagIcon}
            title={search || statusFilter ? t('common.noResults') : t('orders.noOrders')}
            actionLabel={search || statusFilter ? t('common.retry') : undefined}
            onAction={search || statusFilter ? () => { setSearch(''); setStatusFilter(''); } : undefined}
          />
        )}
      </Card>

      {/* Bulk actions bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-xl border border-gray-200 bg-white px-5 py-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{selected.size} {t('common.selected')}</span>
          <Dropdown
            trigger={
              <Button variant="primary" size="sm">{t('orders.changeStatus')}</Button>
            }
            items={orderStatuses.map((s) => ({
              label: s.label,
              onClick: () => setBulkStatus(s.value as OrderStatus),
            }))}
          />
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
            {t('common.cancel')}
          </Button>
        </div>
      )}

      {/* Bulk confirm dialog */}
      <ConfirmDialog
        isOpen={!!bulkStatus}
        onClose={() => setBulkStatus(null)}
        onConfirm={handleBulkConfirm}
        title={t('orders.bulkUpdate')}
        message={t('orders.bulkConfirmStatusChange', { count: selected.size, status: bulkStatus ? t(`orders.statuses.${bulkStatus}`, { defaultValue: bulkStatus }) : '' })}
        confirmLabel={t('common.confirm')}
        loading={bulkMutation.isPending}
      />
    </div>
  );
}
